/**
 * Anti-Inspect Protection Layer
 *
 * Simple logic:
 * - If URL has ?debug=true → Protection OFF (allow DevTools)
 * - Otherwise → Protection ON (redirect to blank page when DevTools opens)
 */

export interface AntiInspectConfig {
  enabled: boolean;
  redirectUrl: string;
  onDetection?: (type: string, details: string) => void;
}

// Callback types for metrics logging
export type MetricsLogCallback = (type: number) => void;

export class AntiInspectLayer {
  private config: AntiInspectConfig;
  private hasRedirected: boolean = false;
  private isDebugMode: boolean = false;
  private isMobileOrEmbedded: boolean = false;
  private detectionIntervals: number[] = [];
  private detectionTimeouts: number[] = [];
  private metricsLogCallback: MetricsLogCallback | null = null;

  /**
   * Docked DevTools shrinks inner vs outer on one axis. Page zoom keeps the viewport
   * filling most of the window, so outer−inner gap jumps from zoom are not treated as DevTools.
   */
  private static readonly ZOOM_LAYOUT_MIN_INNER_OUTER_RATIO = 0.88;

  // Log type constants for metrics
  public static readonly LOG_TYPE_DEBUG_SCREEN = 1;
  public static readonly LOG_TYPE_RIGHT_CLICK = 2;

  constructor(config: Partial<AntiInspectConfig> = {}) {
    // Check debug mode from URL first
    this.isDebugMode = this.checkDebugMode();

    // Check if running on mobile device or embedded in a webview/iframe
    this.isMobileOrEmbedded = this.checkMobileOrEmbedded();

    this.config = {
      enabled: true,
      redirectUrl: 'about:blank',
      onDetection: (type: string, details: string) => {
        console.warn(`[AntiInspect] ${type}: ${details}`);
      },
      ...config,
    };

    // Start protection immediately if not in debug mode
    if (this.config.enabled && !this.isDebugMode) {
      this.startProtection();
    }
  }

  /**
   * Check if running on mobile device or embedded in a webview/iframe
   * Size-based DevTools detection is unreliable in these environments
   */
  private checkMobileOrEmbedded(): boolean {
    try {
      // Check if embedded in iframe
      const isInIframe = window.self !== window.top;

      // Check for mobile devices via user agent
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
      const isMobile = mobileRegex.test(userAgent);

      // Check for touch device as additional indicator
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Check for common webview indicators
      const isWebView =
        // Android WebView
        /wv/.test(userAgent) ||
        // iOS WebView (not Safari)
        (/iPhone|iPod|iPad/.test(userAgent) && !/Safari/.test(userAgent)) ||
        // Facebook/Instagram/TikTok in-app browsers
        /FBAN|FBAV|Instagram|TikTok|Line|Telegram|WhatsApp|WeChat|MicroMessenger/i.test(userAgent) ||
        // Generic WebView detection
        (window as any).ReactNativeWebView !== undefined ||
        (window as any).webkit?.messageHandlers !== undefined ||
        (window as any).Android !== undefined;

      // Check screen size (small screens are likely mobile)
      const isSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;

      // Consider it mobile/embedded if any of these conditions are met
      return isInIframe || isMobile || isWebView || (isTouchDevice && isSmallScreen);
    } catch (e) {
      // If detection fails, assume it might be mobile/embedded to be safe
      return false;
    }
  }

  /**
   * Check if debug mode is enabled via URL param
   * Only ?debug=true will disable protection
   */
  private checkDebugMode(): boolean {
    // try {
    //   const urlParams = new URLSearchParams(window.location.search);
    //   return urlParams.get('debug') === 'true';
    // } catch (e) {
    //   return false;
    // }

    return true;
  }

  /**
   * Start all protection methods
   */
  private startProtection(): void {
    if (this.isDebugMode || this.hasRedirected) return;

    // Block keyboard shortcuts
    this.blockKeyboardShortcuts();

    // Block right-click context menu
    this.blockContextMenu();

    // Start DevTools detection (multiple methods for reliability)
    this.startDevToolsDetection();
  }

  /**
   * Block all keyboard shortcuts that open DevTools
   * Only blocks the shortcuts, does NOT redirect
   * Redirect only happens when DevTools is actually detected as open
   */
  private blockKeyboardShortcuts(): void {
    const handler = (e: KeyboardEvent) => {
      if (this.isDebugMode || this.hasRedirected) return;

      let shouldBlock = false;
      let reason = '';

      // F12
      if (e.key === 'F12' || e.keyCode === 123) {
        shouldBlock = true;
        reason = 'F12 pressed';
      }

      // Ctrl+Shift+I or Cmd+Option+I (DevTools)
      if (
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.keyCode === 73)) ||
        (e.metaKey && e.altKey && (e.key === 'I' || e.key === 'i' || e.keyCode === 73))
      ) {
        shouldBlock = true;
        reason = 'DevTools shortcut (Ctrl/Cmd+Shift/Alt+I)';
      }

      // Ctrl+Shift+J or Cmd+Option+J (Console)
      if (
        (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j' || e.keyCode === 74)) ||
        (e.metaKey && e.altKey && (e.key === 'J' || e.key === 'j' || e.keyCode === 74))
      ) {
        shouldBlock = true;
        reason = 'Console shortcut (Ctrl/Cmd+Shift/Alt+J)';
      }

      // Ctrl+Shift+C or Cmd+Option+C (Inspect Element)
      if (
        (e.ctrlKey && e.shiftKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67)) ||
        (e.metaKey && e.altKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67))
      ) {
        shouldBlock = true;
        reason = 'Inspect Element shortcut (Ctrl/Cmd+Shift/Alt+C)';
      }

      // Ctrl+U or Cmd+Option+U (View Source)
      if (
        (e.ctrlKey && !e.shiftKey && !e.altKey && (e.key === 'U' || e.key === 'u' || e.keyCode === 85)) ||
        (e.metaKey && e.altKey && (e.key === 'U' || e.key === 'u' || e.keyCode === 85))
      ) {
        shouldBlock = true;
        reason = 'View Source shortcut (Ctrl/Cmd+U)';
      }

      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Only log, don't redirect - redirect will happen when DevTools is actually detected
        this.config.onDetection?.('shortcut_blocked', reason);
        // Send metrics log for debug screen attempt (type 1)
        this.triggerMetricsLog(AntiInspectLayer.LOG_TYPE_DEBUG_SCREEN);
        return false;
      }
    };

    // Use capture phase for earliest interception
    document.addEventListener('keydown', handler, true);
    window.addEventListener('keydown', handler, true);
  }

  /**
   * Block right-click context menu
   */
  private blockContextMenu(): void {
    const handler = (e: MouseEvent) => {
      if (this.isDebugMode || this.hasRedirected) return;

      e.preventDefault();
      e.stopPropagation();
      this.config.onDetection?.('context_menu', 'Right-click blocked');
      // Send metrics log for right-click (type 2)
      this.triggerMetricsLog(AntiInspectLayer.LOG_TYPE_RIGHT_CLICK);
      return false;
    };

    document.addEventListener('contextmenu', handler, true);
  }

  /**
   * Start DevTools detection
   */
  private startDevToolsDetection(): void {
    if (this.isDebugMode) return;

    // Skip size-based detection on mobile/embedded environments
    // Size difference detection causes false positives due to:
    // - Mobile browser chrome (address bar, navigation bar)
    // - Webview/iframe container UI elements
    // - Dynamic UI changes (keyboard, orientation, etc.)
    if (this.isMobileOrEmbedded) {
      this.config.onDetection?.(
        'info',
        'Size-based DevTools detection disabled for mobile/embedded environment'
      );
      return;
    }

    // Check for pre-opened DevTools immediately
    this.checkPreOpenedDevTools();

    // Start continuous detection for DevTools opened after page load
    this.startContinuousDetection();

    // Undocked / already-attached DevTools: no viewport resize on this window
    this.startDebuggerDevtoolsProbe();
  }

  /**
   * Skip aggressive size probes when page zoom / visualViewport pinch likely (false positives vs DevTools).
   */
  private shouldSkipAbsoluteSizeDevToolsProbe(): boolean {
    if (this.isDebugMode || this.isMobileOrEmbedded) return false;

    try {
      const vv = (window as Window & { visualViewport?: { scale: number } }).visualViewport;
      if (vv && (vv.scale < 0.995 || vv.scale > 1.005)) {
        return true;
      }
    } catch (e) {
      // ignore
    }

    const dpr = window.devicePixelRatio || 1;
    const nearestInt = Math.round(dpr);
    if (nearestInt >= 1 && Math.abs(dpr - nearestInt) > 0.06) {
      return true;
    }

    return false;
  }

  private getMinInnerOuterRatio(): number {
    const ow = window.outerWidth;
    const oh = window.outerHeight;
    if (ow <= 0 || oh <= 0) return 0;
    return Math.min(window.innerWidth / ow, window.innerHeight / oh);
  }

  private isLikelyPageZoomLayoutVsDockedDevtools(): boolean {
    return (
      this.shouldSkipAbsoluteSizeDevToolsProbe() ||
      this.getMinInnerOuterRatio() > AntiInspectLayer.ZOOM_LAYOUT_MIN_INNER_OUTER_RATIO
    );
  }

  /**
   * Undocked DevTools does not resize the page. When DevTools is attached, `debugger` pauses
   * until Resume so elapsed time spikes; when closed it is a no-op. `Function` body avoids
   * production minifiers dropping bare `debugger` statements.
   */
  private startDebuggerDevtoolsProbe(): void {
    if (this.isDebugMode || this.isMobileOrEmbedded) return;

    const intervalMs = 6200;
    const pauseMs = 200;

    const probe = () => {
      if (this.hasRedirected) return;
      const start = performance.now();
      try {
        (new Function('debugger') as () => void)();
      } catch (e) {
        // ignore
      }
      if (performance.now() - start > pauseMs) {
        this.triggerRedirect('DevTools detected (debugger)');
      }
    };

    const firstProbeId = window.setTimeout(probe, 4000);
    this.detectionTimeouts.push(firstProbeId);
    const id = window.setInterval(probe, intervalMs);
    this.detectionIntervals.push(id);
  }

  /**
   * Check if DevTools was already open before page loaded
   * Uses absolute size difference - if too large, DevTools is likely open
   */
  private checkPreOpenedDevTools(): void {
    if (this.shouldSkipAbsoluteSizeDevToolsProbe()) {
      this.config.onDetection?.(
        'info',
        'Pre-open DevTools size probe skipped (non-default zoom / visual viewport scale)'
      );
      return;
    }
    // Threshold for detecting pre-opened DevTools
    // Normal browser chrome is usually < 100px difference
    // DevTools docked adds 200-400px+ to the difference
    const preOpenedThreshold = 200;
    let consecutiveDetections = 0;
    const requiredDetections = 3;

    const check = () => {
      if (this.isDebugMode || this.hasRedirected) return;

      const ow = window.outerWidth;
      const oh = window.outerHeight;
      const iw = window.innerWidth;
      const ih = window.innerHeight;
      const widthDiff = ow - iw;
      const heightDiff = oh - ih;
      const wRatio = ow > 0 ? iw / ow : 1;
      const hRatio = oh > 0 ? ih / oh : 1;

      const largeChromeGap = widthDiff > preOpenedThreshold || heightDiff > preOpenedThreshold;
      if (
        largeChromeGap &&
        Math.min(wRatio, hRatio) > AntiInspectLayer.ZOOM_LAYOUT_MIN_INNER_OUTER_RATIO
      ) {
        consecutiveDetections = 0;
        return;
      }

      if (largeChromeGap) {
        consecutiveDetections++;
        if (consecutiveDetections >= requiredDetections) {
          this.triggerRedirect('DevTools detected (pre-opened)');
        }
      } else {
        consecutiveDetections = 0;
      }
    };

    // Check multiple times immediately after page load
    // Using short intervals to detect quickly
    const intervalId = window.setInterval(() => {
      check();
    }, 300);

    this.detectionIntervals.push(intervalId);
    const timeoutId = window.setTimeout(() => {
      clearInterval(intervalId);
    }, 5000);
    this.detectionTimeouts.push(timeoutId);

    // Also run initial check after DOM ready
    if (document.readyState === 'complete') {
      check();
    } else {
      window.addEventListener('load', check);
    }
  }

  /**
   * Continuous detection for DevTools opened after page load
   * Detects changes in window size difference
   */
  private startContinuousDetection(): void {
    const changeThreshold = 150; // Lower threshold for detecting change
    const resizeStabilizeMs = 1200;
    let consecutiveDetections = 0;
    const requiredDetections = 3;

    // Store baseline after a delay (to exclude pre-opened DevTools)
    let baselineWidthDiff = 0;
    let baselineHeightDiff = 0;
    let baselineSet = false;
    let lastResizeAt = 0;
    let baselineResetTimer: number | null = null;

    const setBaseline = () => {
      if (this.hasRedirected) return;
      baselineWidthDiff = window.outerWidth - window.innerWidth;
      baselineHeightDiff = window.outerHeight - window.innerHeight;
      baselineSet = true;
    };

    const check = () => {
      if (this.isDebugMode || this.hasRedirected || !baselineSet) return;
      if (Date.now() - lastResizeAt < resizeStabilizeMs) return;

      const currentWidthDiff = window.outerWidth - window.innerWidth;
      const currentHeightDiff = window.outerHeight - window.innerHeight;

      // Detect if difference increased significantly (DevTools opened)
      const widthChange = currentWidthDiff - baselineWidthDiff;
      const heightChange = currentHeightDiff - baselineHeightDiff;

      if (widthChange > changeThreshold || heightChange > changeThreshold) {
        if (this.isLikelyPageZoomLayoutVsDockedDevtools()) {
          baselineWidthDiff = currentWidthDiff;
          baselineHeightDiff = currentHeightDiff;
          consecutiveDetections = 0;
          return;
        }
        consecutiveDetections++;
        if (consecutiveDetections >= requiredDetections) {
          this.triggerRedirect('DevTools detected (size change)');
        }
      } else {
        if (consecutiveDetections > 0) {
          consecutiveDetections--;
        }
      }
    };

    // Set baseline after page stabilizes (2 seconds)
    const initBaselineTimeoutId = window.setTimeout(setBaseline, 2000);
    this.detectionTimeouts.push(initBaselineTimeoutId);

    // Start continuous checking after baseline is set
    const startCheckTimeoutId = window.setTimeout(() => {
      const intervalId = window.setInterval(check, 500);
      this.detectionIntervals.push(intervalId);
    }, 2500);
    this.detectionTimeouts.push(startCheckTimeoutId);

    // Menu → More Tools → Developer Tools also triggers resize. Do not set baseline to the
    // post-open layout (that hides the outer−inner jump and breaks detection).
    window.addEventListener('resize', () => {
      lastResizeAt = Date.now();
      consecutiveDetections = 0;
      if (baselineResetTimer) {
        clearTimeout(baselineResetTimer);
      }
      baselineResetTimer = window.setTimeout(() => {
        if (!baselineSet) {
          setBaseline();
          return;
        }
        const newWidthDiff = window.outerWidth - window.innerWidth;
        const newHeightDiff = window.outerHeight - window.innerHeight;
        const widthJump = newWidthDiff - baselineWidthDiff;
        const heightJump = newHeightDiff - baselineHeightDiff;
        if (widthJump > changeThreshold || heightJump > changeThreshold) {
          if (this.isLikelyPageZoomLayoutVsDockedDevtools()) {
            baselineWidthDiff = newWidthDiff;
            baselineHeightDiff = newHeightDiff;
            return;
          }
          this.triggerRedirect('DevTools detected (resize)');
          return;
        }
        baselineWidthDiff = newWidthDiff;
        baselineHeightDiff = newHeightDiff;
      }, resizeStabilizeMs);
      this.detectionTimeouts.push(baselineResetTimer);
    });
  }

  /**
   * Trigger redirect to blank page
   */
  private triggerRedirect(reason: string): void {
    if (this.hasRedirected || this.isDebugMode) return;

    this.hasRedirected = true;
    this.config.onDetection?.('devtools_detected', reason);
    // Send metrics log for debug screen open (type 1)
    this.triggerMetricsLog(AntiInspectLayer.LOG_TYPE_DEBUG_SCREEN);

    // Clear all intervals
    this.detectionIntervals.forEach((id) => clearInterval(id));

    // Clear page content immediately
    try {
      document.documentElement.innerHTML = '';
    } catch (e) {}

    // Redirect to blank page
    try {
      window.location.replace(this.config.redirectUrl);
    } catch (e) {
      try {
        window.location.href = this.config.redirectUrl;
      } catch (e2) {
        document.write('');
        document.close();
      }
    }
  }

  /**
   * Initialize protection (can be called manually if not auto-started)
   */
  public initialize(): void {
    if (!this.config.enabled || this.isDebugMode) return;
    this.startProtection();
  }

  /**
   * Destroy the protection layer
   */
  public destroy(): void {
    this.detectionIntervals.forEach((id) => clearInterval(id));
    this.detectionIntervals = [];
    this.detectionTimeouts.forEach((id) => clearTimeout(id));
    this.detectionTimeouts = [];
  }

  /**
   * Check if debug mode is active
   */
  public isDebugModeActive(): boolean {
    return this.isDebugMode;
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<AntiInspectConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get statistics
   */
  public getStatistics(): {
    isDebugMode: boolean;
    hasRedirected: boolean;
    enabled: boolean;
    isMobileOrEmbedded: boolean;
  } {
    return {
      isDebugMode: this.isDebugMode,
      hasRedirected: this.hasRedirected,
      enabled: this.config.enabled,
      isMobileOrEmbedded: this.isMobileOrEmbedded,
    };
  }

  /**
   * Check if mobile/embedded mode is active
   */
  public isMobileOrEmbeddedMode(): boolean {
    return this.isMobileOrEmbedded;
  }

  /**
   * Set callback for metrics logging
   * This callback will be called when debug screen is detected or right-click occurs
   */
  public setMetricsLogCallback(callback: MetricsLogCallback): void {
    this.metricsLogCallback = callback;
  }

  /**
   * Trigger metrics log callback
   */
  private triggerMetricsLog(type: number): void {
    if (this.metricsLogCallback) {
      try {
        this.metricsLogCallback(type);
      } catch (e) {
        console.warn('[AntiInspect] Error in metrics log callback:', e);
      }
    }
  }
}
